import {
  Box,
  Typography,
  Button,
  Collapse,
  IconButton,
  CircularProgress,
} from "@mui/material";
import TextField from "components/common/TextField";
import { PlanDetail } from "components/hooks/useTaskAgentState";
import { CircleCheck, CircleHelp, X, LockKeyhole, Plus } from "lucide-react";
import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { SecretRevealConfirmation } from "components/secrets/SecretRevealConfirmation";
import { Family } from "protogen/advisors_service_pb";

export interface PlanDetailsSectionRef {
  openEdit: () => void;
}

// Plan Details Component
export const PlanDetailsSection = forwardRef<
  PlanDetailsSectionRef,
  {
    details: PlanDetail[] | undefined;
    onUpdateDetails?: (updatedDetails: PlanDetail[]) => void;
    isActive?: boolean;
    canRevealSecrets?: boolean;
    family?: Family;
    onEditingChange?: (isEditing: boolean) => void;
  }
>(
  (
    {
      details,
      onUpdateDetails,
      isActive = false,
      canRevealSecrets = false,
      family,
      onEditingChange,
    },
    ref,
  ) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValues, setEditValues] = useState<Record<number, string>>({});
    const [excludedIndices, setExcludedIndices] = useState<Set<number>>(
      new Set(),
    );
    const [touchedFields, setTouchedFields] = useState<Set<number>>(new Set());
    const [originalDetails, setOriginalDetails] = useState<PlanDetail[]>([]);
    const hasAutoOpened = useRef(false);
    const [selectedSecretRef, setSelectedSecretRef] = useState<string | null>(
      null,
    );
    const [newDetails, setNewDetails] = useState<
      Array<{ id: string; title: string; value: string }>
    >([]);

    useEffect(() => {
      onEditingChange?.(isEditing);
    }, [isEditing, onEditingChange]);

    // Auto-open edit mode when details first arrive with empty values
    useEffect(() => {
      if (
        !details ||
        details.length === 0 ||
        !onUpdateDetails ||
        hasAutoOpened.current
      ) {
        return;
      }

      const visibleDetails = details.filter((d) => !d.isExcluded);
      const hasEmptyDetails = visibleDetails.some(
        (d) => !d.value || d.value.trim() === "",
      );
      if (hasEmptyDetails) {
        setOriginalDetails([...details]);

        // Initialize edit values with current values (only non-excluded)
        const initialValues: Record<number, string> = {};
        details.forEach((detail, index) => {
          if (!detail.isExcluded) {
            initialValues[index] = detail.value || "";
          }
        });
        setEditValues(initialValues);
        setTouchedFields(new Set());
        setIsEditing(true);
        hasAutoOpened.current = true;
      }
    }, [details, onUpdateDetails]);

    // Handle new details arriving while form is open
    useEffect(() => {
      if (!isEditing || !details || originalDetails.length === 0) return;

      const originalTitles = new Set(originalDetails.map((d) => d.title));
      const newEditValues = { ...editValues };
      let hasChanges = false;

      details.forEach((detail, index) => {
        if (
          !originalTitles.has(detail.title) &&
          editValues[index] === undefined
        ) {
          // This is a new detail that arrived after form opened
          newEditValues[index] = detail.value || "";
          hasChanges = true;
        }
      });

      if (hasChanges) {
        setEditValues(newEditValues);
      }
    }, [details, isEditing, originalDetails, editValues]);

    if (!details || details.length === 0) return null;

    const handleEdit = () => {
      if (!details) return;

      setOriginalDetails([...details]);

      // Initialize edit values with current values (only non-excluded)
      const initialValues: Record<number, string> = {};
      const excluded = new Set<number>();
      details.forEach((detail, index) => {
        if (!detail.isExcluded) {
          initialValues[index] = detail.value || "";
        } else {
          excluded.add(index);
        }
      });
      setEditValues(initialValues);
      setExcludedIndices(excluded);
      setTouchedFields(new Set());
      setNewDetails([]);
      setIsEditing(true);
    };

    const addNewDetail = () => {
      setNewDetails((prev) => [
        ...prev,
        {
          id: Math.random().toString(),
          title: "",
          value: "",
        },
      ]);
    };

    const updateNewDetail = (
      id: string,
      field: "title" | "value",
      value: string,
    ) => {
      setNewDetails((prev) =>
        prev.map((detail) =>
          detail.id === id ? { ...detail, [field]: value } : detail,
        ),
      );
    };

    const removeNewDetail = (id: string) => {
      setNewDetails((prev) => prev.filter((detail) => detail.id !== id));
    };

    useImperativeHandle(ref, () => ({
      openEdit: handleEdit,
    }));

    const handleContinue = () => {
      if (!onUpdateDetails || !details) return;
      const originalTitles = new Set(originalDetails.map((d) => d.title));

      const updatedDetails = details.map((detail, index) => {
        const wasOriginalField = originalTitles.has(detail.title);
        const wasThisFieldTouched = touchedFields.has(index);

        let finalValue: string;
        if (wasOriginalField && wasThisFieldTouched) {
          // User touched this field → use their edit
          finalValue = editValues[index] ?? detail.value ?? "";
        } else {
          // Either: (1) untouched original field → preserve AI updates
          //         (2) new field that arrived → include it
          finalValue = detail.value || "";
        }

        return {
          ...detail,
          value: finalValue,
          isExcluded: excludedIndices.has(index),
        };
      });

      const validNewDetails: PlanDetail[] = newDetails
        .filter((d) => d.title.trim() && d.value.trim())
        .map((d) => ({
          title: d.title.trim(),
          value: d.value.trim(),
          isExcluded: false,
        }));

      onUpdateDetails([...updatedDetails, ...validNewDetails]);
      setIsEditing(false);
      setExcludedIndices(new Set());
      setTouchedFields(new Set());
      setOriginalDetails([]);
      setNewDetails([]);
    };

    const handleSkip = () => {
      setIsEditing(false);
      setEditValues({});
      setExcludedIndices(new Set());
      setTouchedFields(new Set());
      setOriginalDetails([]);
      setNewDetails([]);
    };

    return (
      <Box>
        <Collapse in={isEditing}>
          <Box
            sx={(theme) => ({
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              padding: "18px 24px",
              backgroundColor: theme.palette.gray[1],
              borderRadius: "8px",
              marginTop: "12px",
            })}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="bodySmallHeavy">
                Add more task details
              </Typography>
              <Button
                variant="text"
                size="small"
                startIcon={<Plus size={14} />}
                onClick={addNewDetail}
                sx={{ height: "unset", minWidth: "auto", padding: "2px 8px" }}
              >
                Add
              </Button>
            </Box>
            {details.map((detail, index) => {
              // Skip if marked as excluded during editing
              if (excludedIndices.has(index)) {
                return null;
              }

              const handleExclude = () => {
                const newExcluded = new Set(excludedIndices);
                newExcluded.add(index);
                setExcludedIndices(newExcluded);
              };

              return (
                <Box
                  key={index}
                  sx={{ display: "flex", gap: "8px", alignItems: "center" }}
                >
                  <TextField
                    label={detail.title}
                    value={
                      editValues[index] !== undefined ? editValues[index] : ""
                    }
                    onChange={(e) => {
                      setEditValues({
                        ...editValues,
                        [index]: e.target.value,
                      });
                      setTouchedFields(new Set(touchedFields).add(index));
                    }}
                    size="small"
                    fullWidth
                    placeholder={`Enter ${(detail.title ?? "").toLowerCase()}`}
                  />
                  <IconButton
                    size="small"
                    onClick={handleExclude}
                    sx={{
                      padding: "4px",
                    }}
                  >
                    <X size={16} />
                  </IconButton>
                </Box>
              );
            })}
            {newDetails.map((newDetail) => (
              <Box
                key={newDetail.id}
                sx={{
                  display: "flex",
                  gap: "8px",
                }}
              >
                <TextField
                  label="Detail name"
                  value={newDetail.title}
                  onChange={(e) =>
                    updateNewDetail(newDetail.id, "title", e.target.value)
                  }
                  size="small"
                  fullWidth
                  placeholder="e.g., Phone number"
                />
                <TextField
                  label="Value"
                  value={newDetail.value}
                  onChange={(e) =>
                    updateNewDetail(newDetail.id, "value", e.target.value)
                  }
                  size="small"
                  fullWidth
                  placeholder="Enter value"
                  sx={{ marginLeft: 0 }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeNewDetail(newDetail.id)}
                  sx={{
                    padding: "4px",
                  }}
                >
                  <X size={16} />
                </IconButton>
              </Box>
            ))}
            {isActive && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <CircularProgress
                  size={14}
                  color="inherit"
                  sx={{ opacity: 0.6 }}
                />
                <Typography variant="bodySmall" color="text.secondary">
                  We are still processing the task and collecting details...
                </Typography>
              </Box>
            )}
            <Box display="flex" gap="16px" justifyContent="flex-end">
              <Button variant="text" onClick={handleSkip} size="small">
                Cancel
              </Button>
              <Button variant="contained" onClick={handleContinue} size="small">
                Update
              </Button>
            </Box>
          </Box>
        </Collapse>

        <Collapse in={!isEditing}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginTop: "8px",
            }}
          >
            {details
              .filter((detail) => !detail.isExcluded)
              .map((detail, index) => {
                const hasValue = detail.value && detail.value.trim() !== "";

                return (
                  <Box
                    key={index}
                    onClick={onUpdateDetails ? handleEdit : undefined}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "wrap",
                      alignItems: "center",
                      padding: "2px 4px",
                      cursor: onUpdateDetails ? "pointer" : "default",
                      "&:hover": onUpdateDetails
                        ? {
                            backgroundColor: "action.hover",
                            borderRadius: "4px",
                          }
                        : {},
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                        marginRight: "4px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: "6px",
                        }}
                      >
                        {hasValue ? (
                          <CircleCheck size={18} color="#468E3E" />
                        ) : (
                          <CircleHelp size={18} color="#8E9598" />
                        )}
                      </Box>
                      <Typography variant="bodySmall">
                        {detail.title}:
                      </Typography>
                    </Box>
                    {hasValue ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          variant="bodySmallHeavy"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {detail.value}
                        </Typography>
                        {detail.ref &&
                          detail.refType === "secret" &&
                          canRevealSecrets &&
                          family && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedSecretRef(detail.ref!);
                              }}
                              sx={{
                                padding: "2px",
                                color: "primary.main",
                              }}
                            >
                              <LockKeyhole size={16} />
                            </IconButton>
                          )}
                      </Box>
                    ) : (
                      <Typography
                        variant="bodySmallHeavy"
                        color="text.tertiary"
                      >
                        Not found
                      </Typography>
                    )}
                  </Box>
                );
              })}
          </Box>
        </Collapse>

        {selectedSecretRef && family && (
          <SecretRevealConfirmation
            open={!!selectedSecretRef}
            onClose={() => setSelectedSecretRef(null)}
            secretRef={selectedSecretRef}
            family={family}
          />
        )}
      </Box>
    );
  },
);
